import { DocsArea, docsAreas } from '../components/docsLink/ReadTheDocsLink'

export type AppManagementArea =
  | 'licenses'
  | 'settings'
  | 'settings/general'
  | 'settings/mongo'
  | 'settings/admin'
  | 'issues'
  | 'metrics'
  | 'collections'
  | 'devices'
  | 'connect'
  | 'cdc'
  | 'cdc/data-bridges'
  | 'cdc/data-bridges/new'
export type OrganizationManagementArea =
  | 'apps'
  | 'databases'
  | 'activity'
  | 'settings'
  | 'new-app'
  | 'new-database'
export type UserTenantArea =
  | 'apps'
  | 'databases'
  | 'organizations'
  | 'settings'
  | 'apps/request-access'
  | 'databases/request-access'
  | 'employee-groups'
export type OrganizationSettingsArea =
  | 'general'
  | 'members'
  | 'roles'
  | 'roles/new'
  | 'access-grants'
  | 'access-grants/new'
export type EmployeeGroupManagementArea = 'customers' | 'employees' | 'settings'

const DOCS_BASE_URL = 'https://docs.ditto.live'

type AppManagementParams = {
  organizationSlug?: string
  tenantId?: string
  appSlug: string
  area?: AppManagementArea
  isRenamed?: boolean
}

const link = {
  app: ({
    organizationSlug,
    tenantId,
    appSlug,
    isRenamed,
  }: {
    organizationSlug?: string
    tenantId?: string
    appSlug: string
    isRenamed?: boolean
  }): string => {
    if (organizationSlug) {
      return `/${organizationSlug}/${appSlug}`
    }
    if (tenantId) {
      // for admins
      return `/u/${tenantId}/${appSlug}`
    }

    // for regular users
    return isRenamed ? `/database/${appSlug}` : `/app/${appSlug}`
  },
  appManagement: ({
    organizationSlug,
    appSlug,
    tenantId,
    area = 'settings',
    isRenamed,
  }: AppManagementParams): string => {
    if (organizationSlug) {
      return `/${organizationSlug}/${appSlug}/${area}`
    }
    if (tenantId) {
      // for admins
      return `/u/${tenantId}/${appSlug}/${area}`
    }

    // for regular users
    return isRenamed
      ? `/database/${appSlug}/${area}`
      : `/app/${appSlug}/${area}`
  },
  appNewIssue: ({
    organizationSlug,
    appSlug,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    tenantId: string,
  }: {
    organizationSlug?: string
    appSlug?: string
    tenantId: string
  }): string => {
    // UNIMPLEMENTED Add support for new issues on user apps, where organizationSlug is falsy but we have an app slug and a tenant ID
    // In that case issues should be handled under /u/:tenantId/:appSlug/issues/new
    return organizationSlug && appSlug
      ? `/${organizationSlug}/${appSlug}/issues/new`
      : '/issues/new'
  },
  appIssue: ({
    organizationSlug,
    appSlug,
    issueId,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    tenantId,
  }: {
    organizationSlug?: string
    appSlug?: string
    tenantId: string
    issueId: string
  }): string => {
    // UNIMPLEMENTED Add support for issues on user apps, where organizationSlug is falsy but we have an app slug and a tenant ID
    // In that case issues should be handled under /u/:tenantId/:appSlug/issues
    return !organizationSlug || !appSlug
      ? `/issues/${issueId}`
      : `/${organizationSlug}/${appSlug}/issues/${issueId}`
  },
  createOrganization: (): string => '/organizations/create',
  createApp: ({
    userId,
    isRenamed,
  }: {
    userId?: string
    isRenamed?: boolean
  }): string =>
    userId
      ? `/u/${userId}/${isRenamed ? 'new-database' : 'new-app'}`
      : `/${isRenamed ? 'new-database' : 'new-app'}`,
  gettingStarted: (): string => '/',
  organizationManagement: (params: {
    slug: string
    area?: OrganizationManagementArea
    isRenamed?: boolean
  }): string => {
    const defaultArea = params.isRenamed ? 'databases' : 'apps'
    let area = params.area

    // Map old areas to new ones if renamed
    if (params.isRenamed && area) {
      if (area === 'apps') {
        area = 'databases'
      } else if (area === 'new-app') {
        area = 'new-database'
      }
    }

    return `/${params.slug}/${area || defaultArea}`
  },
  organizationSettings: (params: {
    slug: string
    area?: OrganizationSettingsArea
  }): string =>
    `/${params.slug}/settings${params.area ? '/' + params.area : ''}`,
  editOrganizationRole: ({ name, slug }: { name: string; slug: string }) =>
    link.organizationSettings({ slug, area: 'roles' }) + `/${name}`,
  createOrganizationApp: (slug: string, isRenamed?: boolean): string =>
    `/${slug}/${isRenamed ? 'new-database' : 'new-app'}`,
  findEmployeeGroup: () => '/find-employee-group',
  forge: ({
    databaseId,
    appId,
  }: {
    databaseId: string
    appId?: string
  }): string =>
    `/forge/${databaseId}${appId ? `/builder/${appId}/design` : ''}`,
  forgeNeue: ({
    databaseId,
    appId,
  }: {
    databaseId: string
    appId?: string
  }): string =>
    `/forge-neue/${databaseId}${appId ? `/builder/${appId}/design` : ''}`,
  createEmployeeGroup: () => '/employee-groups/new',
  employeeGroupManagement: (params: {
    slug: string
    area?: EmployeeGroupManagementArea
  }) => `/employee-groups/${params.slug}/${params.area || 'customers'}`,
  appCDC: (params: Omit<AppManagementParams, 'area'>) =>
    link.appManagement({ area: 'cdc', ...params }),
  // TODO(2367): ID in URL sort of breaks our existing patterns, but we don't store a slug
  // or a required, friendly identifier for streams. We should consider changing this.
  appDataBridge: ({
    id,
    ...params
  }: Omit<AppManagementParams, 'area'> & { id: string }) =>
    link
      .appManagement({ area: 'cdc/data-bridges', ...params })
      .concat(`/${id}`),
  createAppDataBridge: (params: Omit<AppManagementParams, 'area'>) =>
    link.appManagement({ area: 'cdc/data-bridges/new', ...params }),
  findOrganization: (): string => '/find-organization',
  settings: (): string => '/settings',
  users: (): string => '/users',
  findApp: (isRenamed?: boolean): string =>
    !!isRenamed ? '/find-database' : '/find-app',
  feedback: (): string => '/feedback',
  dqlEditor: ({ appId, dql }: { appId: string; dql?: string }): string =>
    `/dql-editor/${appId}${dql ? `?dql=${encodeURIComponent(dql)}` : ''}`,
  dataBrowser: ({
    appId,
    collection,
  }: {
    appId: string
    collection?: string
  }): string =>
    `/data-browser/${appId}${collection ? `?collection=${collection}` : ''}`,
  deviceDashboard: ({
    appSlug,
    organizationSlug,
    peerKey,
    userId,
  }: {
    appSlug: string
    organizationSlug?: string
    peerKey?: string
    userId?: string
  }) =>
    `${link.appManagement({
      appSlug,
      organizationSlug,
      tenantId: userId,
      area: 'devices',
    })}${peerKey ? `/${peerKey}` : ''}`,
  userApp: ({ userId, appSlug }: { userId: string; appSlug: string }): string =>
    `/u/${userId}/${appSlug}`,
  userApps: ({
    userId,
    isRenamed,
  }: {
    userId?: string
    isRenamed?: boolean
  }): string =>
    userId
      ? `/u/${userId}/${isRenamed ? 'databases' : 'apps'}`
      : `/${isRenamed ? 'databases' : 'apps'}`,
  userTenant: ({ userId, area }: { userId?: string; area: UserTenantArea }) =>
    userId ? `/u/${userId}/${area}` : `/${area}`,
  userOrganizations: ({ userId }: { userId?: string }): string =>
    userId ? `/u/${userId}/organizations` : '/organizations',
  dittoDocs: (area?: DocsArea) =>
    `${DOCS_BASE_URL}/${area ? docsAreas[area] || '' : ''}`,
  contactUs: (): string => 'https://support.ditto.live',
  support: () => 'https://support.ditto.live',
  notifications: () => '/notifications',
} as const

export default link

/** Returns true if the path is a 'static path' that doesn't cause a redirect
 *  when the tenant context changes.
 */
export function isStaticPath(path: string, isRenamed?: boolean): boolean {
  const staticPaths = [
    link.findOrganization(),
    link.users(),
    link.findApp(!!isRenamed),
    link.feedback(),
    link.support(),
    link.notifications(),
  ]

  return staticPaths.includes(path)
}
